import { default as React } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, Content } from '@backstage/core-components';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import {
  TbBuildingStore,
  TbServer2,
  TbLayoutDashboard,
  TbCircleCheck,
  TbPlayerPlay,
  TbTemplate,
  TbExternalLink,
  TbChevronRight,
} from 'react-icons/tb';
import { Badge, Button, brand } from '@ciandtspike.io/plugin-ui';
import { MARKETPLACE_ROUTES } from '@ciandtspike.io/plugin-ciandtspike-common';
import { ciandtspikeOnboardingTranslationRef } from '../translation';

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === 'dark';

  // prototype tokens
  const mutedText = isDark ? brand.textMuted.dark : brand.textMuted.light;
  const strongText = isDark ? '#ffffff' : '#111827';
  const dividerLine = isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0';
  const stepIconBg = isDark ? brand.surface.dark : '#dadde9';
  const stepIconColor = isDark ? brand.surfaceText.dark : brand.navy;

  // "How it works" card — bg-primary-50 dark:bg-white/[0.06]
  const howCardBg = isDark ? 'rgba(255,255,255,0.06)' : '#f0f4ff';
  const howCardBorder = isDark ? 'rgba(255,255,255,0.12)' : '#c7d2f5';
  // large step numbers — text-primary-200 dark:text-white/40
  const bigStepColor = isDark ? 'rgba(255,255,255,0.40)' : '#c7d2f5';
  // pipeline pills — bg-primary-100 dark:bg-primary-800
  const pipelinePillBg = isDark ? brand.navy : '#dadde9';
  const pipelinePillText = isDark ? '#c7d2f5' : brand.navy;

  // "New templates" dashed card
  const templateCardBg = isDark ? 'rgba(255,255,255,0.06)' : '#fcfdfe';
  const templateCardBorder = isDark ? 'rgba(255,255,255,0.12)' : '#dadde9';
  const templateIconColor = isDark ? '#c7d2f5' : '#6366f1';
  const templateLinkBorder = isDark ? 'rgba(255,255,255,0.12)' : brand.navy;
  const templateLinkText = isDark ? '#d1d5db' : brand.navy;

  return {
    root: {
      maxWidth: 820,
      margin: '0 auto',
      padding: theme.spacing(3, 0),
    },

    // ── Header ────────────────────────────────────────────────────────────
    headerTitle: {
      fontWeight: 800,
      marginBottom: theme.spacing(1),
      color: strongText,
    },
    headerSubtitle: {
      color: mutedText,
      lineHeight: 1.6,
    },

    // ── Getting started — timeline ────────────────────────────────────────
    section: {
      marginBottom: theme.spacing(6),
    },
    // text-heading-sm = 20px/28px — MUI h6 é 18px, corrigido aqui
    sectionTitle: {
      fontSize: 20,
      lineHeight: '28px',
      fontWeight: 700,
      marginBottom: theme.spacing(3),
      color: strongText,
    },
    timelineRow: {
      display: 'flex',
      gap: theme.spacing(2.5),
    },
    timelineIconCol: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      flexShrink: 0,
    },
    timelineIconCircle: {
      width: 32,
      height: 32,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      backgroundColor: stepIconBg,
      color: stepIconColor,
    },
    timelineConnector: {
      width: 1,
      flex: 1,
      margin: theme.spacing(0.5, 0),
      backgroundColor: dividerLine,
    },
    timelineContent: {
      flex: 1,
      minWidth: 0,
    },
    timelineContentPadded: {
      paddingBottom: theme.spacing(4),
    },
    timelineStepTitle: {
      fontWeight: 700,
      marginBottom: theme.spacing(0.5),
      color: strongText,
    },
    timelineStepDesc: {
      color: mutedText,
      lineHeight: 1.6,
    },
    timelineCtaLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      marginTop: theme.spacing(1.5),
      fontSize: '0.75rem',
      fontWeight: 700,
      color: isDark ? brand.navyLightText : brand.navy,
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0,
      '&:hover': { opacity: 0.75 },
    },

    // ── How it works — blue card ──────────────────────────────────────────
    howCard: {
      borderRadius: 12,
      padding: theme.spacing(3),
      backgroundColor: howCardBg,
      border: `1px solid ${howCardBorder}`,
    },
    howGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: theme.spacing(3),
      marginBottom: theme.spacing(3),
      [theme.breakpoints.down('xs')]: {
        gridTemplateColumns: '1fr',
      },
    },
    howStepNumber: {
      fontSize: 40,
      fontWeight: 800,
      lineHeight: 1,
      marginBottom: theme.spacing(1),
      userSelect: 'none',
      color: bigStepColor,
    },
    howStepTitle: {
      fontWeight: 700,
      marginBottom: theme.spacing(0.5),
      color: strongText,
    },
    howStepBody: {
      color: mutedText,
      lineHeight: 1.5,
    },
    pipelineStrip: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.75),
      overflowX: 'auto',
      whiteSpace: 'nowrap',
    },
    pipelinePill: {
      padding: theme.spacing(0.5, 1.5),
      borderRadius: 9999,
      fontSize: '0.75rem',
      fontWeight: 600,
      backgroundColor: pipelinePillBg,
      color: pipelinePillText,
    },
    pipelineArrow: {
      color: isDark ? '#6b7280' : '#a0aec0',
      fontWeight: 700,
      fontSize: '0.875rem',
      userSelect: 'none',
    },

    // ── What's available today — 2-col grid ───────────────────────────────
    availableGrid: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr',
      gap: theme.spacing(4),
      alignItems: 'stretch',
      [theme.breakpoints.down('xs')]: {
        gridTemplateColumns: '1fr',
      },
    },
    availableItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.5),
      '& + &': { marginTop: theme.spacing(2.5) },
    },
    checkIcon: {
      color: isDark ? brand.success.dark : brand.success.light,
      flexShrink: 0,
      marginTop: 2,
    },
    availableLabel: {
      fontWeight: 500,
      color: strongText,
    },
    availableSub: {
      color: mutedText,
      display: 'block',
    },

    // "New templates" dashed card
    templateCard: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      padding: theme.spacing(2),
      borderRadius: 12,
      backgroundColor: templateCardBg,
      border: `1px dashed ${templateCardBorder}`,
      height: '100%',
      boxSizing: 'border-box',
    },
    templateIcon: {
      color: templateIconColor,
      flexShrink: 0,
    },
    templateTitle: {
      fontWeight: 700,
      marginBottom: theme.spacing(0.5),
      color: strongText,
    },
    templateDesc: {
      color: mutedText,
      lineHeight: 1.5,
    },
    templateLink: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      marginTop: 'auto',
      padding: theme.spacing(1, 1.5),
      borderRadius: 8,
      fontSize: '0.75rem',
      fontWeight: 700,
      border: `1px solid ${templateLinkBorder}`,
      color: templateLinkText,
      textDecoration: 'none',
      transition: 'background-color 120ms ease',
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f0f4ff',
      },
    },

    header: {
      marginBottom: theme.spacing(5),
    },
    headerBadgeWrap: {
      marginBottom: theme.spacing(1.5),
    },
    ctaBannerTitle: {
      fontWeight: 700,
      color: '#ffffff',
      marginBottom: 2,
    },
    ctaBannerSubtitle: {
      color: 'rgba(255,255,255,0.6)',
      display: 'block',
    },

    // ── CTA banner ────────────────────────────────────────────────────────
    ctaBanner: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      padding: theme.spacing(2.5),
      borderRadius: 12,
      backgroundColor: brand.navy,
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
  };
});

export const NewOnboardingPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const { t } = useTranslationRef(ciandtspikeOnboardingTranslationRef);
  const configApi = useApi(configApiRef);
  const templateRequestUrl = configApi.getOptionalString(
    'app.onboarding.templateRequestUrl',
  );

  const STEPS = [
    {
      icon: TbBuildingStore,
      title: t('gettingStarted.step1.title'),
      description: t('gettingStarted.step1.description'),
      cta: {
        label: t('gettingStarted.step1.cta'),
        to: MARKETPLACE_ROUTES.root,
      },
    },
    {
      icon: TbServer2,
      title: t('gettingStarted.step2.title'),
      description: t('gettingStarted.step2.description'),
      cta: null,
    },
    {
      icon: TbLayoutDashboard,
      title: t('gettingStarted.step3.title'),
      description: t('gettingStarted.step3.description'),
      cta: {
        label: t('gettingStarted.step3.cta'),
        to: MARKETPLACE_ROUTES.provisionedResources,
      },
    },
  ];

  const HOW_IT_WORKS = [
    {
      step: '1',
      title: t('howItWorks.step1.title'),
      body: t('howItWorks.step1.body'),
    },
    {
      step: '2',
      title: t('howItWorks.step2.title'),
      body: t('howItWorks.step2.body'),
    },
    {
      step: '3',
      title: t('howItWorks.step3.title'),
      body: t('howItWorks.step3.body'),
    },
    {
      step: '4',
      title: t('howItWorks.step4.title'),
      body: t('howItWorks.step4.body'),
    },
  ];

  const PIPELINE = [
    t('flowPipeline.step1'),
    t('flowPipeline.step2'),
    t('flowPipeline.step3'),
    t('flowPipeline.step4'),
    t('flowPipeline.step5'),
  ];

  const AVAILABLE_ITEMS = [
    {
      label: t('availableToday.item1.label'),
      sub: t('availableToday.item1.sub'),
    },
    {
      label: t('availableToday.item2.label'),
      sub: t('availableToday.item2.sub'),
    },
    {
      label: t('availableToday.item3.label'),
      sub: t('availableToday.item3.sub'),
    },
  ];

  return (
    <Page themeId="tool">
      <Content>
        <div className={classes.root}>
          {/* ── Header ──────────────────────────────────────────────── */}
          <div className={classes.header}>
            <div className={classes.headerBadgeWrap}>
              <Badge
                background={isDark ? brand.surface.dark : brand.surface.light}
                color={
                  isDark ? brand.surfaceText.dark : brand.surfaceText.light
                }
              >
                {t('header.badge')}
              </Badge>
            </div>
            <Typography variant="h5" className={classes.headerTitle}>
              {t('header.title')}
            </Typography>
            <Typography variant="body2" className={classes.headerSubtitle}>
              {t('header.subtitle')}
            </Typography>
          </div>

          {/* ── Getting started — timeline ───────────────────────── */}
          <section className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              {t('gettingStarted.title')}
            </Typography>
            <div>
              {STEPS.map(({ icon: Icon, title, description, cta }, i) => {
                const isLast = i === STEPS.length - 1;
                return (
                  <div key={title} className={classes.timelineRow}>
                    <div className={classes.timelineIconCol}>
                      <div className={classes.timelineIconCircle}>
                        <Icon size={16} />
                      </div>
                      {!isLast && <div className={classes.timelineConnector} />}
                    </div>
                    <div
                      className={[
                        classes.timelineContent,
                        !isLast && classes.timelineContentPadded,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <Typography
                        variant="body2"
                        className={classes.timelineStepTitle}
                      >
                        {title}
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.timelineStepDesc}
                      >
                        {description}
                      </Typography>
                      {cta && (
                        <button
                          type="button"
                          onClick={() => navigate(cta.to)}
                          className={classes.timelineCtaLink}
                        >
                          {cta.label}
                          <TbChevronRight size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── How it works — blue card ─────────────────────────── */}
          <section className={classes.section}>
            <div className={classes.howCard}>
              <Typography variant="h6" className={classes.sectionTitle}>
                {t('howItWorks.title')}
              </Typography>
              <div className={classes.howGrid}>
                {HOW_IT_WORKS.map(({ step, title, body }) => (
                  <div key={step}>
                    <div className={classes.howStepNumber}>{step}</div>
                    <Typography
                      variant="body2"
                      className={classes.howStepTitle}
                    >
                      {title}
                    </Typography>
                    <Typography
                      variant="caption"
                      className={classes.howStepBody}
                    >
                      {body}
                    </Typography>
                  </div>
                ))}
              </div>
              <div className={classes.pipelineStrip}>
                {PIPELINE.map((label, i) => (
                  <React.Fragment key={label}>
                    <span className={classes.pipelinePill}>{label}</span>
                    {i < PIPELINE.length - 1 && (
                      <span className={classes.pipelineArrow} aria-hidden>
                        →
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </section>

          {/* ── What's available today ────────────────────────────── */}
          <section className={classes.section}>
            <div className={classes.availableGrid}>
              <div>
                <Typography variant="h6" className={classes.sectionTitle}>
                  {t('availableToday.title')}
                </Typography>
                {AVAILABLE_ITEMS.map(({ label, sub }) => (
                  <div key={label} className={classes.availableItem}>
                    <TbCircleCheck className={classes.checkIcon} size={18} />
                    <div>
                      <Typography
                        variant="body2"
                        className={classes.availableLabel}
                      >
                        {label}
                      </Typography>
                      <Typography
                        variant="caption"
                        className={classes.availableSub}
                        style={{ display: 'block' }}
                      >
                        {sub}
                      </Typography>
                    </div>
                  </div>
                ))}
              </div>

              {/* New templates — dashed card */}
              <div className={classes.templateCard}>
                <TbTemplate
                  className={classes.templateIcon}
                  size={20}
                  aria-hidden
                />
                <div>
                  <Typography variant="body2" className={classes.templateTitle}>
                    {t('newTemplates.title')}
                  </Typography>
                  <Typography variant="body2" className={classes.templateDesc}>
                    {t('newTemplates.description')}
                  </Typography>
                </div>
                {templateRequestUrl && (
                  <a
                    href={templateRequestUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.templateLink}
                  >
                    {t('newTemplates.cta')}
                    <TbExternalLink size={12} aria-hidden />
                  </a>
                )}
              </div>
            </div>
          </section>

          {/* ── CTA banner ───────────────────────────────────────── */}
          <div className={classes.ctaBanner}>
            <div>
              <Typography variant="body2" className={classes.ctaBannerTitle}>
                {t('cta.title')}
              </Typography>
              <Typography
                variant="caption"
                className={classes.ctaBannerSubtitle}
              >
                {t('cta.subtitle')}
              </Typography>
            </div>
            <Button
              variant="outline"
              startIcon={<TbPlayerPlay size={16} />}
              onClick={() => navigate(MARKETPLACE_ROUTES.root)}
            >
              {t('cta.button')}
            </Button>
          </div>
        </div>
      </Content>
    </Page>
  );
};
